import { useFieldArray, UseFormReturn } from "react-hook-form";
import { InviteStaffMemberFormValues } from "./useInviteStaffMemberForm";
import { useEffect } from "react";

type InviteForm = UseFormReturn<{
  sendInvitationEmail: boolean;
  toInvite: {
    email: string;
    firstName: string;
    lastName: string;
  }[];
}>;

type UseInviteStaffProps = {
  form: UseFormReturn<{
    sendInvitationEmail: boolean;
    toInvite: {
      email: string;
      firstName: string;
      lastName: string;
    }[];
  }>;
  onClose: () => void;
};

export const useInviteStaff = ({ form, onClose }: UseInviteStaffProps) => {
  // Get the array of items from the zod template
  const { fields, append, remove } = useFieldArray<InviteStaffMemberFormValues>(
    {
      control: form.control,
      name: "toInvite",
    },
  );

  /**
   * Adds a new empty staff member to the array
   * @returns
   */
  const handleAddStaffMember = () =>
    append({
      email: "",
      firstName: "",
      lastName: "",
    });

  /**
   * Removes a staff member from the array
   * @param index
   * @returns
   */
  const handleRemoveStaffMember = (index: number) => remove(index);

  /**
   * Ensure that the array contains at least 1 staff member
   * - Resetting the form will add a new element with the default values
   */
  useEffect(() => {
    if (fields.length === 0) {
      form.reset();
    }
  }, [fields, form]);

  /**
   * Handle closing the modal and resetting the form
   * @param form
   * @param defaultValues
   */
  const handleCloseClick = (
    form: InviteForm,
    defaultValues: InviteStaffMemberFormValues,
  ) => {
    form.reset(defaultValues);
    onClose();
  };

  return {
    staffMembersToInvite: fields,
    handleAddStaffMember,
    handleRemoveStaffMember,
    handleCloseClick,
  };
};
